import { Injectable } from '@angular/core';
import { StorageMap } from '@ngx-pwa/local-storage';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { BundleType, EntityTypeId, FieldName, PreSignRequestData, SlideType } from '../models';
import { HttpService } from './http.service';
import { MediaObject } from '../models/media-object.model';

@Injectable({
  providedIn: 'root'
})
export class MediaPresignApiService {
  constructor(
    private readonly httpService: HttpService,
    private readonly storage: StorageMap
  ) { }

  public getPreSignedUrl(
    entityTypeId: EntityTypeId,
    bundle: BundleType | SlideType,
    fieldName: FieldName,
    media: Partial<MediaObject>,
    preSignedToken?: string
  ): Observable<string> {

    if (!media) {
      return of('')
    };

    const data = {
      entityTypeId,
      bundle,
      fieldName,
      key: media.key
    };

    const preSignHashData = media.preSignHashData;

    if (preSignHashData) {
      Object.assign(data, { preSignHashData });
    }

    if (preSignedToken) {
      Object.assign(data, { preSignedToken });
    }

    const fetch$ = this.httpService.post<{ url: string }>(`get-pre-signed-url`, data);
    return this.presignedUrlFromCache(data, fetch$);
  }

  public getPreSignedUrlCookies(
    entityTypeId: EntityTypeId,
    bundle: BundleType | SlideType,
    fieldName: FieldName,
    media: Partial<MediaObject>,
    preSignedToken?: string,
    useOriginalKey = false
  ): Observable<string> {

    if (!media) {
      return of('')
    };

    const data = {
      entityTypeId,
      bundle,
      fieldName,
      key: media.streamKey || media.key
    };

    const preSignHashData = media.preSignHashData;

    if (preSignHashData) {
      Object.assign(data, { preSignHashData });
    }

    if (preSignedToken) {
      Object.assign(data, { preSignedToken });
    }

    return this.httpService.post<{ url: string }>('get-pre-signed-cookies', data, true)
      .pipe(
        map(res => res?.url)
      );
  }

  public getPreSignedUrlMulti(items: PreSignRequestData[]): Observable<string[]> {
    return this.httpService.post<{ url: string }[]>(`multipart-pre-signed-url`, { items }, true)
      .pipe(
        map(items => {
          return items.map(item => item.url)
        })
      )
  }

  private presignedUrlFromCache(data: any, fetch$: Observable<{url: string}>): Observable<string> {
    const getQueryParamFromUrl = (url: string, queryParam: string) =>
      url?.split('&')?.find(item => item.indexOf(`${queryParam}=`) !== -1)?.split('=')?.pop();

    const isUrlActive = (cachedDate: { url: string }): boolean => {
      if (!cachedDate) {
        return false;
      }

      const { url } = cachedDate;
      
      const expirationDate = +(getQueryParamFromUrl(url, 'Llab-Expires') || 0) * 1000;
      const now = new Date().getTime();
      const epxirationBuffer = 10 * 1000;

      return expirationDate > (now + epxirationBuffer);
    };

    fetch$ = fetch$.pipe(
      tap((res: { url: string }) => {
        if (isUrlActive(res)) {
          this.storage.set(data.key, res).subscribe();
        }
      })
    );

    return this.storage.get(data.key).pipe(
      mergeMap((storageRes: any) => isUrlActive(storageRes) ? of(storageRes) : fetch$),
      map((res: { url: string }) => res.url)
    );
  }
}
